import { Component, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, map } from 'rxjs/operators';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { DownloadSectionComponent } from './components/download-section/download-section.component';
import { LeftNavMenuComponent } from './components/left-nav-menu/left-nav-menu.component';
import { CommonModule } from '@angular/common';
import { FirstVisitPopupComponent } from './components/first-visit-popup/first-visit-popup.component';
import { UserProfileService } from './services/user-profile.service';
import { LoggingService } from './services/logging.service';
import { DebugDashboardComponent } from './components/debug-dashboard/debug-dashboard.component';
import { LoaderComponent } from './components/loader/loader.component';
import { LoaderService } from './services/loader.service';

import { Analytics, isSupported } from '@angular/fire/analytics';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [HeaderComponent, FooterComponent, DownloadSectionComponent, LeftNavMenuComponent, RouterOutlet, CommonModule, FirstVisitPopupComponent, DebugDashboardComponent, LoaderComponent],
  template: `
    <div class="app-layout">
      <app-header #appHeader></app-header>
      <div class="main-content-wrapper" #mainContentWrapper>
        <app-left-nav-menu></app-left-nav-menu>
        <div class="content-area">
          <router-outlet></router-outlet>
        </div>
      </div>
      <app-footer></app-footer>
      <button *ngIf="showBackToTop" class="back-to-top" (click)="scrollToTop()">
        Back to Top
      </button>
      <app-first-visit-popup *ngIf="showPopup" (popupClosed)="onClosePopup()"></app-first-visit-popup>
      <app-debug-dashboard *ngIf="showDebugDashboard"></app-debug-dashboard>
      <app-loader *ngIf="(isLoading$ | async)"></app-loader>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }

    .app-layout {
      display: grid;
      grid-template-rows: auto 1fr auto;
      min-height: 100vh;
    }

    .main-content-wrapper {
      display: flex;
      overflow: auto; /* Ensures content within this area can scroll if needed */
      margin-top: var(--header-height, 80px);
    }
    .content-area {
      margin-left: 220px;
      padding: 20px;
      flex-grow: 1;
    }
    .back-to-top {
      position: fixed;
      bottom: 20px;
      right: 20px;
      background-color: #ed8b00;
      color: white;
      border: none;
      border-radius: 5px;
      padding: 10px 20px;
      cursor: pointer;
      z-index: 1000;
    }
    .back-to-top:hover {
      background-color: #d47c00;
    }
    @media (max-width: 992px) {
      .content-area {
        margin-left: 0;
      }
    }
    @media (max-width: 768px) {
      .main-content-wrapper {
        flex-direction: column;
        margin-top: 80px;
      }
    }
  `]
})
export class AppComponent implements OnInit {
  @ViewChild('appHeader', { read: ElementRef }) headerRef!: ElementRef;
  @ViewChild('mainContentWrapper', { read: ElementRef }) mainContentWrapperRef!: ElementRef;

  title = 'tuneme-angular-app';
  showBackToTop = false;
  showPopup = false;
  showDebugDashboard = false;
  isLoading$ = this.loaderService.isLoading$;

  constructor(
    private router: Router,
    private titleService: Title,
    private userProfileService: UserProfileService,
    private loggingService: LoggingService,
    public loaderService: LoaderService,
    private analytics: Analytics
  ) {}

  ngOnInit() {
    if (!this.userProfileService.getProfileData()) {
      this.showPopup = true;
    }

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Set page title
      let route = this.router.routerState.root;
      while (route.firstChild) {
        route = route.firstChild;
      }
      const title = route.snapshot.data['title'] || 'Tune Me';
      this.titleService.setTitle(title);

      isSupported().then((supported: boolean) => {
        if (supported) {
          console.log('Firebase Analytics is supported');
        } else {
          console.log('Firebase Analytics is not supported');
        }
      });

      // Log navigation
      this.loggingService.info(`Navigated to ${event.urlAfterRedirects}`);

      
    });
  }

  onClosePopup() {
    this.showPopup = false;
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.showBackToTop = window.pageYOffset > 100;
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
